/**
 * Utility Helper Functions
 *
 * Collection of commonly used utility functions for the frontend application
 */

import { format, formatDistance, formatDistanceToNow, parseISO, isValid, differenceInDays } from 'date-fns';
import { usePage } from '@inertiajs/vue3';

// ==============================================
// TIMEZONE UTILITIES
// ==============================================

/**
 * Get company timezone from Inertia page props
 * Falls back to UTC if not available
 * @returns {string} IANA timezone identifier (e.g., 'Asia/Dubai', 'America/New_York')
 */
export function getCompanyTimezone() {
    try {
        const page = usePage();
        // Try multiple paths to find timezone
        return page.props.auth?.company?.timezone?.name
            || page.props.app?.timezone
            || 'UTC';
    } catch (error) {
        console.error('Error getting company timezone:', error);
        return 'UTC';
    }
}

/**
 * Format date in a specific timezone using native Intl API
 * @param {Date|string|null} date - Date object or ISO string
 * @param {Object|string} options - Intl.DateTimeFormat options object or preset name ('short', 'long', 'date', 'time', 'datetime')
 * @param {string} timeZone - IANA timezone identifier (defaults to company timezone)
 * @returns {string}
 */
export function formatInTimezone(date, options = 'datetime', timeZone = null) {
    if (!date) return '';

    try {
        const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
        if (!isValid(dateObj)) return '';

        const tz = timeZone || getCompanyTimezone();

        // Preset format options
        const presets = {
            'short': {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            },
            'long': {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
                timeZoneName: 'short'
            },
            'date': {
                year: 'numeric',
                month: 'short',
                day: 'numeric'
            },
            'time': {
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            },
            'datetime': {
                year: 'numeric',
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false
            }
        };

        // Use preset or custom options
        const formatOptions = typeof options === 'string'
            ? presets[options] || presets.datetime
            : options;

        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            ...formatOptions
        });

        return formatter.format(dateObj);
    } catch (error) {
        console.error('Error formatting date in timezone:', error);
        return '';
    }
}

/**
 * Get timezone offset in minutes for a specific date
 * Handles DST automatically
 * @param {string} timeZone - IANA timezone identifier
 * @param {Date} date - Date to check (defaults to now)
 * @returns {number} Offset in minutes from UTC (positive = ahead of UTC, negative = behind UTC)
 */
export function getTimezoneOffset(timeZone, date = new Date()) {
    try {
        // Get the date/time string in UTC
        const utcDate = new Date(date.toLocaleString('en-US', { timeZone: 'UTC' }));
        // Get the date/time string in target timezone
        const tzDate = new Date(date.toLocaleString('en-US', { timeZone }));
        // Calculate difference in minutes
        return (tzDate.getTime() - utcDate.getTime()) / 60000;
    } catch (error) {
        console.error('Error getting timezone offset:', error);
        return 0;
    }
}

/**
 * Convert user input from company timezone to UTC for storage
 * Important: Use this when saving datetime-local input values to ensure correct UTC storage
 * @param {string|Date} dateInput - Date string or Date object in company timezone
 * @param {string} timeZone - IANA timezone the input is in (defaults to company timezone)
 * @returns {Date} UTC Date object
 */
export function convertToUTC(dateInput, timeZone = null) {
    try {
        const tz = timeZone || getCompanyTimezone();

        // Parse the input date
        const localDate = typeof dateInput === 'string' ? new Date(dateInput) : new Date(dateInput);

        if (!isValid(localDate)) {
            throw new Error('Invalid date input');
        }

        // Get the offset for this specific date/time in the timezone
        const offset = getTimezoneOffset(tz, localDate);

        // Adjust the date by subtracting the offset to get UTC
        const utcTime = localDate.getTime() - (offset * 60000);
        return new Date(utcTime);
    } catch (error) {
        console.error('Error converting to UTC:', error);
        return new Date(); // Fallback to current time
    }
}

/**
 * Get date in company timezone for datetime-local input fields
 * Converts UTC date to company timezone for display in input
 * @param {Date|string|null} utcDate - UTC date
 * @param {string} timeZone - IANA timezone (defaults to company timezone)
 * @returns {string} Format: "YYYY-MM-DDTHH:mm" suitable for datetime-local input
 */
export function formatForDateTimeInput(utcDate, timeZone = null) {
    if (!utcDate) return '';

    try {
        const dateObj = typeof utcDate === 'string' ? parseISO(utcDate) : new Date(utcDate);
        if (!isValid(dateObj)) return '';

        const tz = timeZone || getCompanyTimezone();

        // Get date/time parts in the target timezone
        const formatter = new Intl.DateTimeFormat('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            hour12: false
        });

        const parts = formatter.formatToParts(dateObj);
        const partsObj = Object.fromEntries(
            parts.filter(p => p.type !== 'literal').map(p => [p.type, p.value])
        );

        // Format as YYYY-MM-DDTHH:mm
        return `${partsObj.year}-${partsObj.month}-${partsObj.day}T${partsObj.hour}:${partsObj.minute}`;
    } catch (error) {
        console.error('Error formatting for datetime input:', error);
        return '';
    }
}

/**
 * Format date using date-fns format strings with timezone awareness
 * Converts UTC date to company timezone, then applies date-fns formatting
 * @param {Date|string|null} date - Date object or ISO string
 * @param {string} formatStr - date-fns format string (e.g., 'MMM dd, yyyy HH:mm')
 * @param {string} timeZone - IANA timezone (defaults to company timezone)
 * @returns {string}
 */
export function formatDateInTimezone(date, formatStr = 'MMM dd, yyyy', timeZone = null) {
    if (!date) return '';

    try {
        const dateObj = typeof date === 'string' ? parseISO(date) : new Date(date);
        if (!isValid(dateObj)) return '';

        const tz = timeZone || getCompanyTimezone();

        // Get the date string in target timezone
        const tzDateString = dateObj.toLocaleString('en-US', {
            timeZone: tz,
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
            hour: '2-digit',
            minute: '2-digit',
            second: '2-digit',
            hour12: false
        });

        // Parse the timezone-adjusted string back to a Date
        const [datePart, timePart] = tzDateString.split(', ');
        const [month, day, year] = datePart.split('/');
        const [hour, minute, second] = timePart.split(':');
        const tzDate = new Date(year, month - 1, day, hour, minute, second);

        // Use date-fns format function
        return format(tzDate, formatStr);
    } catch (error) {
        console.error('Error formatting date with date-fns in timezone:', error);
        return '';
    }
}

// ==============================================
// DATE & TIME FORMATTING (Enhanced with timezone support)
// ==============================================

/**
 * Format a date with a given format string
 * Now timezone-aware! Automatically uses company timezone unless specified
 * @param {Date|string|null} date - Date object or ISO string
 * @param {string} formatStr - Format string (date-fns format)
 * @param {string|null} timeZone - IANA timezone (optional, uses company timezone if not provided)
 * @returns {string}
 */
export function formatDate(date, formatStr = 'MMM dd, yyyy', timeZone = null) {
    if (!date) return '';

    try {
        // If timezone is explicitly provided or we can get company timezone, use timezone-aware formatting
        const tz = timeZone !== null ? timeZone : getCompanyTimezone();

        // Always use timezone-aware formatting now
        return formatDateInTimezone(date, formatStr, tz);
    } catch (error) {
        console.error('Error formatting date:', error);
        return '';
    }
}

/**
 * Format a date with time
 * Timezone-aware - uses company timezone by default
 * @param {Date|string|null} date - Date object or ISO string
 * @param {string|null} timeZone - IANA timezone (optional, uses company timezone if not provided)
 * @returns {string}
 */
export function formatDateTime(date, timeZone = null) {
    return formatDate(date, 'MMM dd, yyyy HH:mm', timeZone);
}

/**
 * Format time only
 * Timezone-aware - uses company timezone by default
 * @param {Date|string|null} date - Date object or ISO string
 * @param {string|null} timeZone - IANA timezone (optional, uses company timezone if not provided)
 * @returns {string}
 */
export function formatTime(date, timeZone = null) {
    return formatDate(date, 'HH:mm', timeZone);
}

/**
 * Format date as relative time (e.g., "2 hours ago")
 * @param {Date|string|null} date - Date object or ISO string
 * @returns {string}
 */
export function formatRelative(date) {
    if (!date) return '';

    try {
        const dateObj = typeof date === 'string' ? parseISO(date) : date;
        if (!isValid(dateObj)) return '';
        return formatDistanceToNow(dateObj, { addSuffix: true });
    } catch (error) {
        console.error('Error formatting relative date:', error);
        return '';
    }
}

/**
 * Alias for formatRelative - Format date as relative time (e.g., "2 hours ago")
 * @param {Date|string|null} date - Date object or ISO string
 * @returns {string}
 */
export const formatRelativeTime = formatRelative;

/**
 * Format date range
 * @param {Date|string} start - Start date
 * @param {Date|string} end - End date
 * @param {string} separator - Separator between dates
 * @returns {string}
 */
export function formatDateRange(start, end, separator = ' - ') {
    const startFormatted = formatDate(start);
    const endFormatted = formatDate(end);

    if (!startFormatted && !endFormatted) return '';
    if (!startFormatted) return endFormatted;
    if (!endFormatted) return startFormatted;

    return `${startFormatted}${separator}${endFormatted}`;
}

/**
 * Get days between two dates
 * @param {Date|string} start - Start date
 * @param {Date|string} end - End date
 * @returns {number}
 */
export function getDaysBetween(start, end) {
    try {
        const startDate = typeof start === 'string' ? parseISO(start) : start;
        const endDate = typeof end === 'string' ? parseISO(end) : end;
        return differenceInDays(endDate, startDate);
    } catch (error) {
        console.error('Error calculating days between:', error);
        return 0;
    }
}

/**
 * Format date for input[type="date"]
 * @param {Date|string|null} date - Date object or ISO string
 * @returns {string} YYYY-MM-DD format
 */
export function formatDateForInput(date) {
    return formatDate(date, 'yyyy-MM-dd');
}

/**
 * Format datetime for input[type="datetime-local"]
 * @param {Date|string|null} date - Date object or ISO string
 * @returns {string} YYYY-MM-DDTHH:mm format
 */
export function formatDateTimeForInput(date) {
    return formatDate(date, "yyyy-MM-dd'T'HH:mm");
}

// ==============================================
// CURRENCY & NUMBER FORMATTING
// ==============================================

/**
 * Format amount as currency using company settings
 * @param {number} amount - Amount to format
 * @param {Object|string} currencyOrCompany - Either a currency object {symbol, code, decimal_places} or a company object with currency relation, or currency code string
 * @param {string} locale - Locale for formatting
 * @returns {string}
 */
export function formatCurrency(amount, currencyOrCompany = null, locale = 'en-US') {
    if (amount === null || amount === undefined) return '';

    // Helper to unwrap Vue Proxy objects and get raw data
    const unwrapProxy = (obj) => {
        if (!obj) return obj;
        // Vue 3 Proxy objects have the raw value accessible via JSON serialization
        return JSON.parse(JSON.stringify(obj));
    };

    // Determine the currency object
    let currency = null;

    if (currencyOrCompany) {
        // If it's a company object with currency
        if (typeof currencyOrCompany === 'object' && currencyOrCompany.currency) {
            // Unwrap the Proxy to get actual data
            const unwrapped = unwrapProxy(currencyOrCompany);
            // Currency might be nested in {data: {...}} due to Resource transformation
            currency = unwrapped.currency.data || unwrapped.currency;
        }
        // If it's a currency object directly
        else if (typeof currencyOrCompany === 'object' && currencyOrCompany.symbol) {
            currency = currencyOrCompany;
        }
        // If it's a currency code string (legacy support)
        else if (typeof currencyOrCompany === 'string') {
            try {
                return new Intl.NumberFormat(locale, {
                    style: 'currency',
                    currency: currencyOrCompany,
                }).format(amount);
            } catch (error) {
                console.error('Error formatting currency:', error);
                return `${currencyOrCompany} ${amount}`;
            }
        }
    }

    // If we have a currency object, use it
    if (currency && currency.symbol && currency.decimal_places !== undefined) {
        const formattedAmount = new Intl.NumberFormat(locale, {
            minimumFractionDigits: currency.decimal_places,
            maximumFractionDigits: currency.decimal_places,
        }).format(amount);

        return `${currency.symbol} ${formattedAmount}`;
    }

    // Fallback to USD
    try {
        return new Intl.NumberFormat(locale, {
            style: 'currency',
            currency: 'USD',
        }).format(amount);
    } catch (error) {
        console.error('Error formatting currency:', error);
        return `$ ${amount}`;
    }
}

/**
 * Format number with specific decimal places
 * @param {number} number - Number to format
 * @param {number} decimals - Number of decimal places
 * @param {string} locale - Locale for formatting
 * @returns {string}
 */
export function formatNumber(number, decimals = 0, locale = 'en-US') {
    if (number === null || number === undefined) return '';

    try {
        return new Intl.NumberFormat(locale, {
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        }).format(number);
    } catch (error) {
        console.error('Error formatting number:', error);
        return number.toString();
    }
}

/**
 * Format number with compact notation (e.g., 1K, 1.5M)
 * @param {number} number - Number to format
 * @param {string} locale - Locale for formatting
 * @returns {string}
 */
export function formatCompactNumber(number, locale = 'en-US') {
    if (number === null || number === undefined) return '';

    try {
        return new Intl.NumberFormat(locale, {
            notation: 'compact',
            compactDisplay: 'short',
        }).format(number);
    } catch (error) {
        console.error('Error formatting compact number:', error);
        return number.toString();
    }
}

/**
 * Format percentage
 * @param {number} value - Value to format as percentage (0-100)
 * @param {number} decimals - Number of decimal places
 * @returns {string}
 */
export function formatPercentage(value, decimals = 0) {
    if (value === null || value === undefined) return '';
    return `${formatNumber(value, decimals)}%`;
}

// ==============================================
// STRING UTILITIES
// ==============================================

/**
 * Truncate string to specified length
 * @param {string} string - String to truncate
 * @param {number} length - Maximum length
 * @param {string} suffix - Suffix to append (default: '...')
 * @returns {string}
 */
export function truncate(string, length = 50, suffix = '...') {
    if (!string) return '';
    if (string.length <= length) return string;
    return string.substring(0, length).trim() + suffix;
}

/**
 * Truncate string to specified number of words
 * @param {string} string - String to truncate
 * @param {number} wordCount - Maximum number of words
 * @param {string} suffix - Suffix to append (default: '...')
 * @returns {string}
 */
export function truncateWords(string, wordCount = 10, suffix = '...') {
    if (!string) return '';
    const words = string.split(' ');
    if (words.length <= wordCount) return string;
    return words.slice(0, wordCount).join(' ') + suffix;
}

/**
 * Capitalize first letter of string
 * @param {string} string - String to capitalize
 * @returns {string}
 */
export function capitalize(string) {
    if (!string) return '';
    return string.charAt(0).toUpperCase() + string.slice(1);
}

/**
 * Capitalize first letter of each word
 * @param {string} string - String to capitalize
 * @returns {string}
 */
export function capitalizeWords(string) {
    if (!string) return '';
    return string
        .split(' ')
        .map(word => capitalize(word))
        .join(' ');
}

/**
 * Convert string to slug (URL-friendly)
 * @param {string} string - String to slugify
 * @returns {string}
 */
export function slugify(string) {
    if (!string) return '';

    return string
        .toLowerCase()
        .trim()
        .replace(/[^\w\s-]/g, '') // Remove non-word chars
        .replace(/[\s_-]+/g, '-')  // Replace spaces/underscores with hyphens
        .replace(/^-+|-+$/g, '');  // Remove leading/trailing hyphens
}

/**
 * Simple pluralization
 * @param {string} word - Word to pluralize
 * @param {number} count - Count to determine singular/plural
 * @param {string} plural - Custom plural form (optional)
 * @returns {string}
 */
export function pluralize(word, count, plural = null) {
    if (count === 1) return word;
    return plural || word + 's';
}

/**
 * Strip HTML tags from string
 * @param {string} html - HTML string
 * @returns {string}
 */
export function stripHtml(html) {
    if (!html) return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
}

/**
 * Escape HTML special characters
 * @param {string} string - String to escape
 * @returns {string}
 */
export function escapeHtml(string) {
    if (!string) return '';
    const div = document.createElement('div');
    div.textContent = string;
    return div.innerHTML;
}

/**
 * Generate random string
 * @param {number} length - Length of random string
 * @returns {string}
 */
export function randomString(length = 10) {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
}

// ==============================================
// FILE UTILITIES
// ==============================================

/**
 * Format file size in human-readable format
 * @param {number} bytes - File size in bytes
 * @param {number} decimals - Number of decimal places
 * @returns {string}
 */
export function formatFileSize(bytes, decimals = 2) {
    if (bytes === 0) return '0 Bytes';
    if (bytes === null || bytes === undefined) return '';

    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
}

/**
 * Get file extension from filename
 * @param {string} filename - Filename
 * @returns {string}
 */
export function getFileExtension(filename) {
    if (!filename) return '';
    return filename.slice((filename.lastIndexOf('.') - 1 >>> 0) + 2).toLowerCase();
}

/**
 * Get filename without extension
 * @param {string} filename - Filename
 * @returns {string}
 */
export function getFileNameWithoutExtension(filename) {
    if (!filename) return '';
    const lastDotIndex = filename.lastIndexOf('.');
    return lastDotIndex === -1 ? filename : filename.substring(0, lastDotIndex);
}

/**
 * Check if file is an image
 * @param {string} filename - Filename
 * @returns {boolean}
 */
export function isImage(filename) {
    if (!filename) return false;
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'svg', 'webp', 'bmp', 'ico'];
    const ext = getFileExtension(filename);
    return imageExtensions.includes(ext);
}

/**
 * Check if file is a document
 * @param {string} filename - Filename
 * @returns {boolean}
 */
export function isDocument(filename) {
    if (!filename) return false;
    const docExtensions = ['pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'csv'];
    const ext = getFileExtension(filename);
    return docExtensions.includes(ext);
}

/**
 * Check if file is a video
 * @param {string} filename - Filename
 * @returns {boolean}
 */
export function isVideo(filename) {
    if (!filename) return false;
    const videoExtensions = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'mkv', 'webm'];
    const ext = getFileExtension(filename);
    return videoExtensions.includes(ext);
}

/**
 * Get MIME type from file extension
 * @param {string} filename - Filename
 * @returns {string}
 */
export function getMimeType(filename) {
    const ext = getFileExtension(filename);
    const mimeTypes = {
        // Images
        jpg: 'image/jpeg',
        jpeg: 'image/jpeg',
        png: 'image/png',
        gif: 'image/gif',
        svg: 'image/svg+xml',
        webp: 'image/webp',
        // Documents
        pdf: 'application/pdf',
        doc: 'application/msword',
        docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        xls: 'application/vnd.ms-excel',
        xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        // Text
        txt: 'text/plain',
        csv: 'text/csv',
        // Video
        mp4: 'video/mp4',
        // Add more as needed
    };
    return mimeTypes[ext] || 'application/octet-stream';
}

// ==============================================
// VALIDATION
// ==============================================

/**
 * Validate email address
 * @param {string} email - Email to validate
 * @returns {boolean}
 */
export function isEmail(email) {
    if (!email) return false;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
}

/**
 * Validate URL
 * @param {string} url - URL to validate
 * @returns {boolean}
 */
export function isURL(url) {
    if (!url) return false;
    try {
        new URL(url);
        return true;
    } catch {
        return false;
    }
}

/**
 * Validate phone number (basic validation)
 * @param {string} phone - Phone number to validate
 * @returns {boolean}
 */
export function isPhoneNumber(phone) {
    if (!phone) return false;
    // International format with optional + and spaces/hyphens
    const phoneRegex = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;
    return phoneRegex.test(phone);
}

/**
 * Check if string contains only numbers
 * @param {string} string - String to check
 * @returns {boolean}
 */
export function isNumeric(string) {
    if (string === null || string === undefined || string === '') return false;
    return !isNaN(parseFloat(string)) && isFinite(string);
}

/**
 * Check if string is empty or whitespace
 * @param {string} string - String to check
 * @returns {boolean}
 */
export function isEmpty(string) {
    return !string || string.trim().length === 0;
}

/**
 * Validate password strength
 * @param {string} password - Password to validate
 * @returns {Object} { isValid: boolean, strength: number, feedback: array }
 */
export function validatePassword(password) {
    if (!password) return { isValid: false, strength: 0, feedback: ['Password is required'] };

    const feedback = [];
    let strength = 0;

    // Length check
    if (password.length >= 8) {
        strength += 25;
    } else {
        feedback.push('Password must be at least 8 characters');
    }

    // Uppercase check
    if (/[A-Z]/.test(password)) {
        strength += 25;
    } else {
        feedback.push('Add uppercase letters');
    }

    // Lowercase check
    if (/[a-z]/.test(password)) {
        strength += 25;
    } else {
        feedback.push('Add lowercase letters');
    }

    // Number or special character check
    if (/[0-9]/.test(password) || /[^A-Za-z0-9]/.test(password)) {
        strength += 25;
    } else {
        feedback.push('Add numbers or special characters');
    }

    return {
        isValid: strength === 100,
        strength,
        feedback,
    };
}

// ==============================================
// ARRAY & OBJECT UTILITIES
// ==============================================

/**
 * Group array of objects by key
 * @param {Array} array - Array to group
 * @param {string} key - Key to group by
 * @returns {Object}
 */
export function groupBy(array, key) {
    if (!Array.isArray(array)) return {};

    return array.reduce((result, item) => {
        const groupKey = item[key];
        if (!result[groupKey]) {
            result[groupKey] = [];
        }
        result[groupKey].push(item);
        return result;
    }, {});
}

/**
 * Sort array of objects by key
 * @param {Array} array - Array to sort
 * @param {string} key - Key to sort by
 * @param {string} direction - 'asc' or 'desc'
 * @returns {Array}
 */
export function sortBy(array, key, direction = 'asc') {
    if (!Array.isArray(array)) return [];

    return [...array].sort((a, b) => {
        const aValue = a[key];
        const bValue = b[key];

        if (aValue < bValue) return direction === 'asc' ? -1 : 1;
        if (aValue > bValue) return direction === 'asc' ? 1 : -1;
        return 0;
    });
}

/**
 * Remove duplicates from array
 * @param {Array} array - Array to deduplicate
 * @param {string} key - Optional key for object arrays
 * @returns {Array}
 */
export function unique(array, key = null) {
    if (!Array.isArray(array)) return [];

    if (key) {
        const seen = new Set();
        return array.filter(item => {
            const value = item[key];
            if (seen.has(value)) return false;
            seen.add(value);
            return true;
        });
    }

    return [...new Set(array)];
}

/**
 * Deep clone an object
 * @param {*} obj - Object to clone
 * @returns {*}
 */
export function deepClone(obj) {
    if (obj === null || typeof obj !== 'object') return obj;
    return JSON.parse(JSON.stringify(obj));
}

/**
 * Check if object is empty
 * @param {Object} obj - Object to check
 * @returns {boolean}
 */
export function isEmptyObject(obj) {
    return obj && Object.keys(obj).length === 0 && obj.constructor === Object;
}

// ==============================================
// DOM & CLIPBOARD UTILITIES
// ==============================================

/**
 * Copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>}
 */
export async function copyToClipboard(text) {
    try {
        if (navigator.clipboard && window.isSecureContext) {
            await navigator.clipboard.writeText(text);
            return true;
        } else {
            // Fallback for older browsers
            const textArea = document.createElement('textarea');
            textArea.value = text;
            textArea.style.position = 'fixed';
            textArea.style.left = '-999999px';
            document.body.appendChild(textArea);
            textArea.focus();
            textArea.select();

            try {
                document.execCommand('copy');
                textArea.remove();
                return true;
            } catch (err) {
                console.error('Fallback: Could not copy text', err);
                textArea.remove();
                return false;
            }
        }
    } catch (err) {
        console.error('Failed to copy:', err);
        return false;
    }
}

/**
 * Download text as file
 * @param {string} text - Text content
 * @param {string} filename - Filename
 * @param {string} mimeType - MIME type
 */
export function downloadFile(text, filename, mimeType = 'text/plain') {
    const blob = new Blob([text], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
}

/**
 * Scroll to element smoothly
 * @param {string|HTMLElement} element - Element or selector
 * @param {Object} options - Scroll options
 */
export function scrollToElement(element, options = {}) {
    const el = typeof element === 'string' ? document.querySelector(element) : element;
    if (!el) return;

    el.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        ...options,
    });
}

// ==============================================
// DEBOUNCE & THROTTLE
// ==============================================

/**
 * Debounce function execution
 * @param {Function} func - Function to debounce
 * @param {number} wait - Wait time in milliseconds
 * @returns {Function}
 */
export function debounce(func, wait = 300) {
    let timeout;
    return function executedFunction(...args) {
        const later = () => {
            clearTimeout(timeout);
            func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
    };
}

/**
 * Throttle function execution
 * @param {Function} func - Function to throttle
 * @param {number} limit - Time limit in milliseconds
 * @returns {Function}
 */
export function throttle(func, limit = 300) {
    let inThrottle;
    return function executedFunction(...args) {
        if (!inThrottle) {
            func(...args);
            inThrottle = true;
            setTimeout(() => inThrottle = false, limit);
        }
    };
}

// ==============================================
// COLOR UTILITIES
// ==============================================

/**
 * Generate random hex color
 * @returns {string}
 */
export function randomColor() {
    return '#' + Math.floor(Math.random() * 16777215).toString(16).padStart(6, '0');
}

/**
 * Get contrast color (black or white) for background
 * @param {string} hexColor - Hex color code
 * @returns {string}
 */
export function getContrastColor(hexColor) {
    if (!hexColor) return '#000000';

    // Remove # if present
    const hex = hexColor.replace('#', '');

    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

    // Return black for light backgrounds, white for dark
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
}

// ==============================================
// MISC UTILITIES
// ==============================================

/**
 * Sleep/delay for specified milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise}
 */
export function sleep(ms) {
    return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * Retry function with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} retries - Number of retries
 * @param {number} delay - Initial delay in milliseconds
 * @returns {Promise}
 */
export async function retry(fn, retries = 3, delay = 1000) {
    try {
        return await fn();
    } catch (error) {
        if (retries === 0) throw error;
        await sleep(delay);
        return retry(fn, retries - 1, delay * 2);
    }
}

/**
 * Generate UUID v4
 * @returns {string}
 */
export function generateUUID() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
        const r = Math.random() * 16 | 0;
        const v = c === 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * Get query parameter from URL
 * @param {string} param - Parameter name
 * @param {string} url - URL (defaults to current URL)
 * @returns {string|null}
 */
export function getQueryParam(param, url = window.location.href) {
    const urlObj = new URL(url);
    return urlObj.searchParams.get(param);
}

/**
 * Build query string from object
 * @param {Object} params - Parameters object
 * @returns {string}
 */
export function buildQueryString(params) {
    return Object.keys(params)
        .filter(key => params[key] !== null && params[key] !== undefined && params[key] !== '')
        .map(key => `${encodeURIComponent(key)}=${encodeURIComponent(params[key])}`)
        .join('&');
}

export default {
    // Timezone utilities
    getCompanyTimezone,
    formatInTimezone,
    getTimezoneOffset,
    convertToUTC,
    formatForDateTimeInput,
    formatDateInTimezone,

    // Date
    formatDate,
    formatDateTime,
    formatTime,
    formatRelative,
    formatRelativeTime,
    formatDateRange,
    getDaysBetween,
    formatDateForInput,
    formatDateTimeForInput,

    // Currency
    formatCurrency,
    formatNumber,
    formatCompactNumber,
    formatPercentage,

    // String
    truncate,
    truncateWords,
    capitalize,
    capitalizeWords,
    slugify,
    pluralize,
    stripHtml,
    escapeHtml,
    randomString,

    // File
    formatFileSize,
    getFileExtension,
    getFileNameWithoutExtension,
    isImage,
    isDocument,
    isVideo,
    getMimeType,

    // Validation
    isEmail,
    isURL,
    isPhoneNumber,
    isNumeric,
    isEmpty,
    validatePassword,

    // Array/Object
    groupBy,
    sortBy,
    unique,
    deepClone,
    isEmptyObject,

    // DOM/Clipboard
    copyToClipboard,
    downloadFile,
    scrollToElement,

    // Functions
    debounce,
    throttle,

    // Color
    randomColor,
    getContrastColor,

    // Misc
    sleep,
    retry,
    generateUUID,
    getQueryParam,
    buildQueryString,
};
